import React, { useState, useEffect, useRef } from 'react';
import { AdminHeader } from './AdminLayout';
import { 
    getGemeinden, 
    getUnterrichtsorte, 
    getRaeume,
    createGemeinde, 
    updateGemeinde, 
    deleteGemeinde,
    createUnterrichtsort,
    updateUnterrichtsort,
    deleteUnterrichtsort,
    createRaum,
    updateRaum,
    deleteRaum,
    getAngebote,
    getLehrpersonen,
    exportStandorteJSON,
    importStandorteJSON,
    checkGemeindeDependencies,
    checkUnterrichtsortDependencies,
    checkRaumDependencies,
    migrateDependencies,
    getErrorMessage
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { 
    Plus, 
    Pencil, 
    Trash2, 
    Building2, 
    MapPin, 
    DoorOpen,
    Search,
    Download,
    Upload,
    Users,
    UserCheck,
    Check,
    X,
    FolderSync,
    FileDown,
    FileUp,
    FileSpreadsheet,
    CheckCircle2,
    AlertCircle,
    Database
} from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';

function parseAnzeigename(bezeichnung) {
    if (!bezeichnung) return '';
    if (bezeichnung.includes('-')) {
        return bezeichnung.split('-').slice(1).join('-').trim();
    }
    return bezeichnung;
}

export function StandorteUebersicht() {
    const [gemeinden, setGemeinden] = useState([]);
    const [unterrichtsorte, setUnterrichtsorte] = useState([]);
    const [raeume, setRaeume] = useState([]);
    const [angebote, setAngebote] = useState([]);
    const [lehrpersonen, setLehrpersonen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGemeinden, setExpandedGemeinden] = useState([]);

    // Sheet/Dialog states
    const [gemeindeSheet, setGemeindeSheet] = useState({ open: false, item: null });
    const [ortSheet, setOrtSheet] = useState({ open: false, item: null, gemeindeId: null });
    const [raumDialog, setRaumDialog] = useState({ open: false, item: null, ortId: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });
    
    // Koordinator Dialog
    const [koordinatorDialog, setKoordinatorDialog] = useState({ open: false, gemeinde: null });
    const [koordinatorSearch, setKoordinatorSearch] = useState('');
    const [selectedKoordinatorIds, setSelectedKoordinatorIds] = useState([]);

    // Forms
    const [gemeindeForm, setGemeindeForm] = useState({ name: '', koordinator_ids: [] });
    const [ortForm, setOrtForm] = useState({ name: '', strasse: '', plz: '', ort: '', anfahrt: '' });
    const [raumForm, setRaumForm] = useState({ bezeichnung: '', anzeigename: '' });

    // Import/Export Dialog
    const [dataDialogOpen, setDataDialogOpen] = useState(false);
    const [dataDialogTab, setDataDialogTab] = useState('export');
    const [jsonImportFile, setJsonImportFile] = useState(null);
    const [jsonImporting, setJsonImporting] = useState(false);
    const [jsonImportResult, setJsonImportResult] = useState(null);
    const fileInputRef = useRef(null);
    const jsonInputRef = useRef(null);

    const fetchData = async () => {
        try {
            const [gemRes, ortRes, raumRes, angRes, lehrRes] = await Promise.all([
                getGemeinden(), getUnterrichtsorte(), getRaeume(), getAngebote(), getLehrpersonen()
            ]);
            setGemeinden(gemRes.data);
            setUnterrichtsorte(ortRes.data);
            setRaeume(raumRes.data);
            setAngebote(angRes.data);
            setLehrpersonen(lehrRes.data);
        } catch (error) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // Build hierarchy
    const hierarchy = gemeinden
        .filter(g => !searchTerm || g.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .map(gemeinde => {
            const orte = unterrichtsorte
                .filter(o => o.gemeinde_id === gemeinde.id)
                .map(ort => {
                    const ortRaeume = raeume.filter(r => r.unterrichtsort_id === ort.id);
                    const angeboteCount = angebote.filter(a => a.unterrichtsort_id === ort.id).length;
                    return { ...ort, raeume: ortRaeume, angeboteCount };
                });
            
            const koordinatorNames = (gemeinde.koordinator_ids || [])
                .map(kid => lehrpersonen.find(l => l.id === kid))
                .filter(Boolean)
                .map(l => `${l.vorname} ${l.nachname}`);
            
            const totalRaeume = orte.reduce((sum, o) => sum + o.raeume.length, 0);
            const totalAngebote = orte.reduce((sum, o) => sum + o.angeboteCount, 0);
            
            return { ...gemeinde, orte, koordinatorNames, totalRaeume, totalAngebote };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

    // Helper to get coordinator name for a location
    const getKoordinatorName = (koordinatorId) => {
        if (!koordinatorId) return null;
        const lp = lehrpersonen.find(l => l.id === koordinatorId);
        return lp ? `${lp.vorname} ${lp.nachname}` : null;
    };

    // Sorted teachers for selection
    const sortedLehrpersonen = [...lehrpersonen].sort((a, b) => 
        `${a.nachname} ${a.vorname}`.localeCompare(`${b.nachname} ${b.vorname}`)
    );

    // Koordinator Dialog handlers
    const openKoordinatorDialog = (gemeinde) => {
        setSelectedKoordinatorIds(gemeinde.koordinator_ids || []);
        setKoordinatorSearch('');
        setKoordinatorDialog({ open: true, gemeinde });
    };

    const toggleKoordinator = (lehrpersonId) => {
        setSelectedKoordinatorIds(prev => 
            prev.includes(lehrpersonId)
                ? prev.filter(id => id !== lehrpersonId)
                : [...prev, lehrpersonId]
        );
    };

    const saveKoordinatoren = async () => {
        if (!koordinatorDialog.gemeinde) return;
        try {
            await updateGemeinde(koordinatorDialog.gemeinde.id, {
                name: koordinatorDialog.gemeinde.name,
                koordinator_ids: selectedKoordinatorIds
            });
            toast.success('Koordinatoren gespeichert');
            setKoordinatorDialog({ open: false, gemeinde: null });
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Speichern'));
        }
    };

    // Dialog handlers
    const openGemeindeSheet = (gemeinde = null) => {
        setGemeindeForm(gemeinde ? { name: gemeinde.name, koordinator_ids: gemeinde.koordinator_ids || [] } : { name: '', koordinator_ids: [] });
        setGemeindeSheet({ open: true, item: gemeinde });
    };

    const openOrtSheet = (ort = null, gemeindeId = null) => {
        setOrtForm(ort ? { 
            name: ort.name, 
            strasse: ort.strasse || '', 
            plz: ort.plz || '', 
            ort: ort.ort || '', 
            anfahrt: ort.anfahrt || ort.hinweis || ''
        } : { name: '', strasse: '', plz: '', ort: '', anfahrt: '' });
        setOrtSheet({ open: true, item: ort, gemeindeId: gemeindeId || ort?.gemeinde_id });
    };

    const openRaumDialog = (raum = null, ortId = null) => {
        setRaumForm(raum ? { bezeichnung: raum.bezeichnung || '', anzeigename: raum.anzeigename || '' } : { bezeichnung: '', anzeigename: '' });
        setRaumDialog({ open: true, item: raum, ortId: ortId || raum?.unterrichtsort_id });
    };

    const openDeleteDialog = (type, item, e) => {
        e?.stopPropagation();
        setDeleteDialog({ open: true, type, item });
    };

    // Save handlers
    const saveGemeinde = async () => {
        try {
            if (gemeindeSheet.item) {
                await updateGemeinde(gemeindeSheet.item.id, gemeindeForm);
                toast.success('Gemeinde gespeichert');
            } else {
                await createGemeinde(gemeindeForm);
                toast.success('Gemeinde erstellt');
            }
            setGemeindeSheet({ open: false, item: null });
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Speichern'));
        }
    };

    const saveOrt = async () => {
        try {
            const data = { 
                name: ortForm.name,
                strasse: ortForm.strasse,
                plz: ortForm.plz,
                ort: ortForm.ort,
                hinweis: ortForm.anfahrt,  // Backend uses 'hinweis'
                gemeinde_id: ortSheet.gemeindeId 
            };
            if (ortSheet.item) {
                await updateUnterrichtsort(ortSheet.item.id, data);
                toast.success('Unterrichtsort gespeichert');
            } else {
                await createUnterrichtsort(data);
                toast.success('Unterrichtsort erstellt');
            }
            setOrtSheet({ open: false, item: null, gemeindeId: null });
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Speichern'));
        }
    };

    const saveRaum = async () => {
        try {
            const data = { 
                bezeichnung: raumForm.bezeichnung,
                anzeigename: raumForm.anzeigename || parseAnzeigename(raumForm.bezeichnung), 
                unterrichtsort_id: raumDialog.ortId
            };
            if (raumDialog.item) {
                await updateRaum(raumDialog.item.id, data);
                toast.success('Raum gespeichert');
            } else {
                await createRaum(data);
                toast.success('Raum erstellt');
            }
            setRaumDialog({ open: false, item: null, ortId: null });
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Speichern'));
        }
    };

    // Delete handler
    const handleDelete = async () => {
        const { type, item } = deleteDialog;
        try {
            if (type === 'gemeinde') await deleteGemeinde(item.id);
            else if (type === 'unterrichtsort') await deleteUnterrichtsort(item.id);
            else if (type === 'raum') await deleteRaum(item.id);
            toast.success('Erfolgreich gelöscht');
            setDeleteDialog({ open: false, type: null, item: null });
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Löschen'));
        }
    };

    // Import/Export Functions
    const handleJSONExport = async () => {
        try {
            const response = await exportStandorteJSON();
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `standorte_backup_${new Date().toISOString().split('T')[0]}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`${gemeinden.length} Gemeinden, ${unterrichtsorte.length} Orte, ${raeume.length} Räume exportiert`);
        } catch (error) {
            toast.error('Export fehlgeschlagen');
        }
    };

    const handleCSVExport = () => {
        const headers = ['Gemeinde', 'Unterrichtsort', 'Straße', 'PLZ', 'Ort', 'Koordinatoren'];
        const rows = [];
        
        gemeinden.forEach(g => {
            const orte = unterrichtsorte.filter(o => o.gemeinde_id === g.id);
            if (orte.length === 0) {
                rows.push([g.name, '', '', '', '', g.koordinatorNames?.join(', ') || '']);
            } else {
                orte.forEach(o => {
                    rows.push([
                        g.name,
                        o.name,
                        o.strasse || '',
                        o.plz || '',
                        o.ort || '',
                        g.koordinatorNames?.join(', ') || ''
                    ]);
                });
            }
        });
        
        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
            .join('\n');
        
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `standorte_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('CSV-Export erfolgreich');
    };

    const handleJSONImport = async () => {
        if (!jsonImportFile) return;
        
        try {
            setJsonImporting(true);
            setJsonImportResult(null);
            
            const text = await jsonImportFile.text();
            const data = JSON.parse(text);
            await importStandorteJSON(data);
            
            setJsonImportResult({ imported: 1, updated: 0, errors: [] });
            toast.success('Import erfolgreich');
            fetchData();
            
            setJsonImportFile(null);
            if (jsonInputRef.current) jsonInputRef.current.value = '';
            
        } catch (error) {
            toast.error('Import fehlgeschlagen: ' + error.message);
            setJsonImportResult({ imported: 0, updated: 0, errors: [{ error: error.message }] });
        } finally {
            setJsonImporting(false);
        }
    };

    const closeDataDialog = () => {
        setDataDialogOpen(false);
        setJsonImportFile(null);
        setJsonImportResult(null);
        if (jsonInputRef.current) jsonInputRef.current.value = '';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#cf2e2e]"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <AdminHeader 
                title="Standorte"
            >
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        onClick={() => setDataDialogOpen(true)} 
                        className="rounded-xl"
                        data-testid="standorte-data-btn"
                    >
                        <FolderSync className="h-4 w-4 mr-2" />
                        Import / Export
                    </Button>
                    <Button onClick={() => openGemeindeSheet()} className="rounded-xl" data-testid="add-gemeinde-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Neue Gemeinde
                    </Button>
                </div>
            </AdminHeader>
            
            <div className="admin-content">
                {/* Search Bar - Unified Style */}
                <div className="flex flex-wrap items-center gap-4 mb-8">
                    <div className="search-wrapper flex-1 max-w-xl">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors" />
                        <Input
                            placeholder="Gemeinde suchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                            data-testid="search-input"
                        />
                    </div>
                    <div className="text-base text-muted-foreground">
                        {hierarchy.length} von {gemeinden.length} Gemeinden
                    </div>
                </div>

                {/* Accordion List */}
                <Accordion 
                    type="multiple" 
                    value={expandedGemeinden}
                    onValueChange={setExpandedGemeinden}
                    className="space-y-3"
                >
                    {hierarchy.map((gemeinde) => (
                        <AccordionItem 
                            key={gemeinde.id} 
                            value={gemeinde.id}
                            className="border border-slate-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow"
                            data-testid={`gemeinde-${gemeinde.id}`}
                        >
                            <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-slate-50/50 rounded-t-xl">
                                <div className="flex items-center gap-4 flex-1">
                                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                        <Building2 className="w-5 h-5 text-slate-600" />
                                    </div>
                                    <div className="text-left flex-1">
                                        <h3 className="text-lg font-semibold text-slate-900">{gemeinde.name}</h3>
                                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
                                            <span>{gemeinde.orte.length} Orte</span>
                                            <span className="text-slate-300">|</span>
                                            <span>{gemeinde.totalRaeume} Räume</span>
                                            <span className="text-slate-300">|</span>
                                            <span>{gemeinde.totalAngebote} Angebote</span>
                                        </div>
                                    </div>
                                    {/* Koordinator Badge - BLAU */}
                                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                        {gemeinde.koordinatorNames && gemeinde.koordinatorNames.length > 0 ? (
                                            <button
                                                onClick={() => openKoordinatorDialog(gemeinde)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
                                                data-testid={`koordinator-${gemeinde.id}`}
                                            >
                                                <UserCheck className="h-4 w-4 text-blue-600" />
                                                <span className="font-medium">{gemeinde.koordinatorNames.join(', ')}</span>
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => openKoordinatorDialog(gemeinde)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200 transition-colors"
                                                data-testid={`koordinator-${gemeinde.id}`}
                                            >
                                                <UserCheck className="h-4 w-4 text-slate-400" />
                                                <span>Koordinator zuweisen</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mr-4" onClick={(e) => e.stopPropagation()}>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-8 w-8 p-0 hover:bg-slate-200"
                                        onClick={() => openGemeindeSheet(gemeinde)}
                                        data-testid={`edit-gemeinde-${gemeinde.id}`}
                                    >
                                        <Pencil className="w-4 h-4 text-slate-500" />
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-8 w-8 p-0 hover:bg-red-100"
                                        onClick={(e) => openDeleteDialog('gemeinde', gemeinde, e)}
                                        data-testid={`delete-gemeinde-${gemeinde.id}`}
                                    >
                                        <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                </div>
                            </AccordionTrigger>
                            
                            <AccordionContent className="px-6 pb-6">
                                {/* Add Location Button */}
                                <div className="flex justify-end mb-4">
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => openOrtSheet(null, gemeinde.id)}
                                        data-testid={`add-ort-${gemeinde.id}`}
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Unterrichtsort
                                    </Button>
                                </div>
                                
                                {gemeinde.orte.length === 0 ? (
                                    <div className="text-center py-8 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                                        <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">Keine Unterrichtsorte vorhanden</p>
                                    </div>
                                ) : (
                                    <div className="grid gap-4 md:grid-cols-2">
                                        {gemeinde.orte.map((ort) => (
                                            <Card 
                                                key={ort.id} 
                                                className="border-amber-200 bg-amber-50/30 hover:bg-amber-50/50 hover:border-amber-300 transition-all"
                                                data-testid={`ort-${ort.id}`}
                                            >
                                                <CardHeader className="pb-3">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-start gap-3">
                                                            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center mt-0.5">
                                                                <MapPin className="w-4 h-4 text-amber-600" />
                                                            </div>
                                                            <div>
                                                                <CardTitle className="text-base font-semibold text-amber-900">
                                                                    {ort.name}
                                                                </CardTitle>
                                                                {(ort.strasse || ort.plz || ort.ort) && (
                                                                    <p className="text-sm text-amber-700/70 mt-0.5">
                                                                        {[ort.strasse, [ort.plz, ort.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm" 
                                                                className="h-7 w-7 p-0 hover:bg-amber-100"
                                                                onClick={() => openOrtSheet(ort)}
                                                            >
                                                                <Pencil className="w-3.5 h-3.5 text-amber-600" />
                                                            </Button>
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm" 
                                                                className="h-7 w-7 p-0 hover:bg-red-100"
                                                                onClick={(e) => openDeleteDialog('unterrichtsort', ort, e)}
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="pt-0">
                                                    {/* Räume Section */}
                                                    <div className="border-t border-amber-200/50 pt-3">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                                                                Räume ({ort.raeume.length})
                                                            </span>
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm" 
                                                                className="h-6 px-2 text-xs text-orange-600 hover:bg-orange-100"
                                                                onClick={() => openRaumDialog(null, ort.id)}
                                                            >
                                                                <Plus className="w-3 h-3 mr-1" />
                                                                Raum
                                                            </Button>
                                                        </div>
                                                        
                                                        {ort.raeume.length === 0 ? (
                                                            <p className="text-sm text-slate-400 italic">Keine Räume</p>
                                                        ) : (
                                                            <div className="flex flex-wrap gap-2">
                                                                {ort.raeume.map((raum) => (
                                                                    <button
                                                                        key={raum.id}
                                                                        onClick={() => openRaumDialog(raum)}
                                                                        data-testid={`raum-${raum.id}`}
                                                                        className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all hover:scale-105 bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100 hover:border-orange-300"
                                                                    >
                                                                        {raum.anzeigename || raum.bezeichnung}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                )}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>

                {hierarchy.length === 0 && (
                    <div className="text-center py-16 text-slate-400">
                        <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg">Keine Gemeinden gefunden</p>
                        {searchTerm && <p className="text-sm mt-1">Versuche einen anderen Suchbegriff</p>}
                    </div>
                )}
            </div>

            {/* Gemeinde Sheet */}
            <Sheet open={gemeindeSheet.open} onOpenChange={(open) => setGemeindeSheet({ ...gemeindeSheet, open })}>
                <SheetContent className="sm:max-w-md">
                    <SheetHeader>
                        <SheetTitle>{gemeindeSheet.item ? 'Gemeinde bearbeiten' : 'Neue Gemeinde'}</SheetTitle>
                    </SheetHeader>
                    <div className="py-6 space-y-4">
                        <div>
                            <Label htmlFor="gemeinde-name">Name *</Label>
                            <Input
                                id="gemeinde-name"
                                value={gemeindeForm.name}
                                onChange={(e) => setGemeindeForm({ ...gemeindeForm, name: e.target.value })}
                                placeholder="z.B. Amstetten"
                                className="mt-1.5"
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label>Koordinator(en)</Label>
                            <div className="mt-1.5 space-y-2 max-h-48 overflow-auto">
                                {lehrpersonen.map(l => (
                                    <label key={l.id} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={gemeindeForm.koordinator_ids.includes(l.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setGemeindeForm({ ...gemeindeForm, koordinator_ids: [...gemeindeForm.koordinator_ids, l.id] });
                                                } else {
                                                    setGemeindeForm({ ...gemeindeForm, koordinator_ids: gemeindeForm.koordinator_ids.filter(id => id !== l.id) });
                                                }
                                            }}
                                            className="rounded"
                                        />
                                        <span className="text-sm">{l.vorname} {l.nachname}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setGemeindeSheet({ open: false, item: null })}>
                            Abbrechen
                        </Button>
                        <Button onClick={saveGemeinde} disabled={!gemeindeForm.name} className="bg-[#cf2e2e] hover:bg-[#b91c1c]">
                            Speichern
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Ort Sheet */}
            <Sheet open={ortSheet.open} onOpenChange={(open) => setOrtSheet({ ...ortSheet, open })}>
                <SheetContent className="sm:max-w-md">
                    <SheetHeader>
                        <SheetTitle>{ortSheet.item ? 'Unterrichtsort bearbeiten' : 'Neuer Unterrichtsort'}</SheetTitle>
                    </SheetHeader>
                    <div className="py-6 space-y-4">
                        <div>
                            <Label htmlFor="ort-name">Name *</Label>
                            <Input
                                id="ort-name"
                                value={ortForm.name}
                                onChange={(e) => setOrtForm({ ...ortForm, name: e.target.value })}
                                placeholder="z.B. Volksschule"
                                className="mt-1.5"
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label htmlFor="ort-strasse">Straße</Label>
                            <Input
                                id="ort-strasse"
                                value={ortForm.strasse}
                                onChange={(e) => setOrtForm({ ...ortForm, strasse: e.target.value })}
                                placeholder="z.B. Hauptstraße 1"
                                className="mt-1.5"
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <Label htmlFor="ort-plz">PLZ</Label>
                                <Input
                                    id="ort-plz"
                                    value={ortForm.plz}
                                    onChange={(e) => setOrtForm({ ...ortForm, plz: e.target.value })}
                                    placeholder="3300"
                                    className="mt-1.5"
                                />
                            </div>
                            <div className="col-span-2">
                                <Label htmlFor="ort-ort">Ort</Label>
                                <Input
                                    id="ort-ort"
                                    value={ortForm.ort}
                                    onChange={(e) => setOrtForm({ ...ortForm, ort: e.target.value })}
                                    placeholder="Amstetten"
                                    className="mt-1.5"
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="ort-anfahrt">Anfahrt</Label>
                            <Textarea
                                id="ort-anfahrt"
                                value={ortForm.anfahrt}
                                onChange={(e) => setOrtForm({ ...ortForm, anfahrt: e.target.value })}
                                placeholder="Hinweise zur Anfahrt..."
                                className="mt-1.5"
                                rows={3}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setOrtSheet({ open: false, item: null, gemeindeId: null })}>
                            Abbrechen
                        </Button>
                        <Button onClick={saveOrt} disabled={!ortForm.name} className="bg-[#cf2e2e] hover:bg-[#b91c1c]">
                            Speichern
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Raum Dialog */}
            <Dialog open={raumDialog.open} onOpenChange={(open) => setRaumDialog({ ...raumDialog, open })}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{raumDialog.item ? 'Raum bearbeiten' : 'Neuer Raum'}</DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div>
                            <Label htmlFor="raum-bezeichnung">Bezeichnung (intern) *</Label>
                            <Input
                                id="raum-bezeichnung"
                                value={raumForm.bezeichnung}
                                onChange={(e) => setRaumForm({ ...raumForm, bezeichnung: e.target.value, anzeigename: raumForm.anzeigename || parseAnzeigename(e.target.value) })}
                                placeholder="z.B. BL-3D OG"
                                className="mt-1.5 font-mono"
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label htmlFor="raum-anzeigename">Anzeigename</Label>
                            <Input 
                                id="raum-anzeigename" 
                                value={raumForm.anzeigename} 
                                onChange={(e) => setRaumForm({ ...raumForm, anzeigename: e.target.value })} 
                                placeholder="z.B. 3D OG" 
                                className="mt-1.5" 
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRaumDialog({ open: false, item: null, ortId: null })}>
                            Abbrechen
                        </Button>
                        <Button onClick={saveRaum} disabled={!raumForm.bezeichnung} className="bg-[#cf2e2e] hover:bg-[#b91c1c]">
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Löschen bestätigen</DialogTitle>
                        <DialogDescription>
                            Möchtest du {deleteDialog.type === 'gemeinde' ? 'die Gemeinde' : deleteDialog.type === 'unterrichtsort' ? 'den Unterrichtsort' : 'den Raum'} "{deleteDialog.item?.name || deleteDialog.item?.bezeichnung}" wirklich löschen?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: null, item: null })}>
                            Abbrechen
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            Löschen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import/Export Dialog */}
            <Dialog open={dataDialogOpen} onOpenChange={closeDataDialog}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl flex items-center gap-2">
                            <Database className="h-5 w-5 text-primary" />
                            Standorte Import / Export
                        </DialogTitle>
                        <DialogDescription>
                            Exportiere Standorte-Daten als Backup oder importiere aus verschiedenen Quellen.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Tabs defaultValue="export" value={dataDialogTab} onValueChange={setDataDialogTab} className="mt-4">
                        <TabsList className="grid w-full grid-cols-2 mb-4">
                            <TabsTrigger value="export" className="flex items-center gap-2">
                                <FileDown className="h-4 w-4" />
                                Exportieren
                            </TabsTrigger>
                            <TabsTrigger value="import" className="flex items-center gap-2">
                                <FileUp className="h-4 w-4" />
                                Importieren
                            </TabsTrigger>
                        </TabsList>
                        
                        {/* EXPORT TAB */}
                        <TabsContent value="export" className="space-y-4">
                            <div className="grid gap-4">
                                {/* JSON Backup Export */}
                                <div className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 bg-emerald-100 rounded-lg">
                                                <Download className="h-5 w-5 text-emerald-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold">Vollständiges Backup (JSON)</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {gemeinden.length} Gemeinden, {unterrichtsorte.length} Orte, {raeume.length} Räume
                                                </p>
                                            </div>
                                        </div>
                                        <Button onClick={handleJSONExport} className="rounded-xl">
                                            Herunterladen
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* CSV Export */}
                                <div className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold">CSV-Export</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Für Excel/Tabellenkalkulation
                                                </p>
                                            </div>
                                        </div>
                                        <Button variant="outline" onClick={handleCSVExport} className="rounded-xl">
                                            Herunterladen
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>
                        
                        {/* IMPORT TAB */}
                        <TabsContent value="import" className="space-y-6">
                            {/* JSON Restore */}
                            <div className="p-4 border rounded-xl">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <Upload className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold">Backup wiederherstellen (JSON)</p>
                                        <p className="text-sm text-muted-foreground">
                                            Aus zuvor exportiertem JSON-Backup. Bestehende Einträge werden aktualisiert.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="file"
                                        accept=".json"
                                        onChange={(e) => setJsonImportFile(e.target.files[0])}
                                        ref={jsonInputRef}
                                        className="flex-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer cursor-pointer"
                                    />
                                    <Button 
                                        onClick={handleJSONImport} 
                                        disabled={!jsonImportFile || jsonImporting}
                                        className="rounded-xl"
                                    >
                                        {jsonImporting ? 'Lädt...' : 'Importieren'}
                                    </Button>
                                </div>
                                {jsonImportResult && (
                                    <div className="mt-3">
                                        {jsonImportResult.imported > 0 ? (
                                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-800">
                                                <CheckCircle2 className="h-5 w-5" />
                                                <span className="font-medium">Import erfolgreich!</span>
                                            </div>
                                        ) : jsonImportResult.errors?.length > 0 && (
                                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                                                <AlertCircle className="h-4 w-4 inline mr-2" />
                                                {jsonImportResult.errors.map(e => e.error).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                    
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={closeDataDialog} className="rounded-xl">
                            Schließen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Koordinator Dialog */}
            <Dialog open={koordinatorDialog.open} onOpenChange={(open) => !open && setKoordinatorDialog({ open: false, gemeinde: null })}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-xl flex items-center gap-2">
                            <UserCheck className="h-5 w-5 text-blue-600" />
                            Koordinator zuweisen
                        </DialogTitle>
                        <DialogDescription>
                            Wähle Koordinatoren für <span className="font-semibold">{koordinatorDialog.gemeinde?.name}</span>
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="py-4 space-y-4">
                        {/* Ausgewählte Koordinatoren */}
                        {selectedKoordinatorIds.length > 0 && (
                            <div className="flex flex-wrap gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                                {selectedKoordinatorIds.map(id => {
                                    const lp = lehrpersonen.find(l => l.id === id);
                                    if (!lp) return null;
                                    return (
                                        <span 
                                            key={id}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                                        >
                                            <UserCheck className="w-3.5 h-3.5" />
                                            {lp.vorname} {lp.nachname}
                                            <button
                                                type="button"
                                                onClick={() => toggleKoordinator(id)}
                                                className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        
                        {/* Suchfeld */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Lehrer suchen..."
                                value={koordinatorSearch}
                                onChange={(e) => setKoordinatorSearch(e.target.value)}
                                className="pl-10"
                                autoFocus
                            />
                        </div>
                        
                        {/* Lehrer-Liste */}
                        <div className="max-h-72 overflow-y-auto border rounded-xl divide-y">
                            {sortedLehrpersonen
                                .filter(lp => {
                                    if (!koordinatorSearch) return true;
                                    const search = koordinatorSearch.toLowerCase();
                                    return lp.nachname?.toLowerCase().includes(search) || 
                                           lp.vorname?.toLowerCase().includes(search);
                                })
                                .map(lp => {
                                    const isSelected = selectedKoordinatorIds.includes(lp.id);
                                    return (
                                        <button
                                            key={lp.id}
                                            type="button"
                                            onClick={() => toggleKoordinator(lp.id)}
                                            className={`w-full text-left px-4 py-3 transition-colors flex items-center gap-3 ${
                                                isSelected 
                                                    ? "bg-blue-50" 
                                                    : "hover:bg-slate-50"
                                            }`}
                                        >
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                isSelected ? "bg-blue-500 text-white" : "bg-slate-100"
                                            }`}>
                                                {isSelected ? (
                                                    <Check className="w-4 h-4" />
                                                ) : (
                                                    <Users className="w-4 h-4 text-slate-400" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`font-medium truncate ${isSelected ? "text-blue-800" : ""}`}>
                                                    {lp.vorname} {lp.nachname}
                                                </p>
                                                {lp.instrumente_names?.length > 0 && (
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {lp.instrumente_names.slice(0, 3).join(', ')}
                                                    </p>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                                    Ausgewählt
                                                </Badge>
                                            )}
                                        </button>
                                    );
                                })
                            }
                        </div>
                        
                        <p className="text-xs text-muted-foreground text-center">
                            Koordinatoren können die Angebote ihrer Gemeinde als PDF herunterladen.
                            <br />
                            <span className="text-blue-600">Mehrfachauswahl möglich.</span>
                        </p>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setKoordinatorDialog({ open: false, gemeinde: null })} className="rounded-xl">
                            Abbrechen
                        </Button>
                        <Button onClick={saveKoordinatoren} className="rounded-xl bg-blue-600 hover:bg-blue-700">
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default StandorteUebersicht;
